import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { trashApi, type RestoreResponse } from '@/api/trash';
import { ApiError } from '@/api/client';
import { formatDateTime } from '@/lib/format';
import { useAuthStore } from '@/stores/auth';

export default function TrashPage() {
  const { t } = useTranslation();
  const tenantId = useAuthStore((s) => s.currentTenantId);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<'notes' | 'credentials'>('notes');
  const [error, setError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['trash', tenantId],
    queryFn: () => trashApi.list(tenantId!),
    enabled: !!tenantId,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['trash', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
    queryClient.invalidateQueries({ queryKey: ['credentials', tenantId] });
  };

  const restoreNote = useMutation({
    mutationFn: (id: string) => trashApi.restoreNote(id),
    onSuccess: (r) => {
      setFeedback(t('trash.restored', { title: r.new_title }));
      setTimeout(() => setFeedback(null), 3000);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.detail : String(err)),
  });
  const restoreCred = useMutation({
    mutationFn: (id: string) => trashApi.restoreCredential(id),
    onSuccess: (r: RestoreResponse) => {
      setFeedback(t('trash.restored', { title: r.new_title }));
      setTimeout(() => setFeedback(null), 3000);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.detail : String(err)),
  });
  if (!tenantId) return <p className="text-sm text-[var(--text-muted)]">{t('vault.no_tenant')}</p>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="display-title">{t('trash.title')}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">
          {t('trash.retention_hint')}
        </p>
      </div>

      {feedback && (
        <div className="text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded px-3 py-2 mb-3">
          {feedback}
        </div>
      )}
      {error && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded px-3 py-2 mb-3">
          {error}
        </div>
      )}

      <div className="flex gap-2 mb-4 border-b border-[var(--border)]">
        <TabBtn active={tab === 'notes'} onClick={() => setTab('notes')}>
          {t('trash.tab_notes')}{' '}
          <span className="text-[10px] text-[var(--text-muted)]">
            ({list.data?.notes.length ?? 0})
          </span>
        </TabBtn>
        <TabBtn
          active={tab === 'credentials'}
          onClick={() => setTab('credentials')}
        >
          {t('trash.tab_credentials')}{' '}
          <span className="text-[10px] text-[var(--text-muted)]">
            ({list.data?.credentials.length ?? 0})
          </span>
        </TabBtn>
      </div>

      <div className="card">
        {list.isLoading && (
          <p className="text-sm text-[var(--text-muted)]">{t('common.loading')}</p>
        )}
        {list.isError && (
          <p className="text-sm text-red-600 dark:text-red-400">
            {t('common.error')}: {String(list.error)}
          </p>
        )}

        {tab === 'notes' && list.data && (
          list.data.notes.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-8">
              {t('trash.no_notes')}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.notes.map((n) => (
                <li key={n.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium">{n.title}</div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {t('trash.deleted_at', { when: formatDateTime(n.deleted_at) })}
                    </div>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => restoreNote.mutate(n.id)}
                      disabled={restoreNote.isPending}
                      className="text-brand-700 dark:text-brand-300 hover:underline disabled:opacity-50"
                    >
                      {t('trash.restore')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )
        )}

        {tab === 'credentials' && list.data && (
          list.data.credentials.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] text-center py-8">
              {t('trash.no_credentials')}
            </p>
          ) : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.credentials.map((c) => (
                <li key={c.id} className="flex items-center justify-between py-3">
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {c.title}
                      <span className="badge bg-[var(--bg-surface)] text-[var(--text-muted)] text-[10px]">
                        {c.type}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--text-muted)]">
                      {t('trash.deleted_at', { when: formatDateTime(c.deleted_at) })}
                    </div>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => restoreCred.mutate(c.id)}
                      disabled={restoreCred.isPending}
                      className="text-brand-700 dark:text-brand-300 hover:underline disabled:opacity-50"
                    >
                      {t('trash.restore')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )
        )}
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
        active
          ? 'border-brand-600 text-brand-700 dark:text-brand-300 font-medium'
          : 'border-transparent text-[var(--text-muted)] hover:text-slate-900 dark:hover:text-slate-100'
      }`}
    >
      {children}
    </button>
  );
}
